/* eslint-disable complexity */
import { SyntheticEvent } from 'react';
import { FieldPath, FieldValues, RegisterOptions, UseFormReturn } from 'react-hook-form';
import { get } from 'lodash-es';
import { isValid } from 'date-fns';

export type CustomRegisterOptions<
	TFieldValues extends FieldValues = FieldValues,
	TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> = RegisterOptions<TFieldValues, TFieldName> & {
	type?: 'text' | 'number' | 'datetime-local' | 'checkbox' | 'radio';
	changePropKey?: string;
	valuePropKey?: string;
	errorPropKey?: string;
	errorFields?: TFieldName[];
	fallbackValue?: any;
	passRef?: boolean;
	passOnBlur?: boolean;
	passType?: boolean;
	min?: number;
	max?: number;
	step?: number;
	digits?: number;
	minLength?: number;
	maxLength?: number;
	minDate?: Date;
	maxDate?: Date;
	ignoreOriginalOnChange?: boolean;
	ignoreOriginalOnBlur?: boolean;
	errorHintPropKey?: string;
	error?: any;
	helperText?: string | null;
	errorOnlyIfTouched?: boolean;
	getEventValue?(e: SyntheticEvent): any;
	transformValue?(value?: any): any;
	transformInputValue?(value?: any): any;
	onChangeAfterTransform?(value?: any): any;
};

export const useCustomRegister = <TFieldValues extends FieldValues = FieldValues>(
	form: UseFormReturn<TFieldValues>
) => {
	const register = <TFieldName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>>(
		key: TFieldName,
		options: CustomRegisterOptions<TFieldValues, TFieldName> = {}
	) => {
		const {
			type = 'text',
			changePropKey = 'onChange',
			valuePropKey = type === 'checkbox' ? 'checked' : 'value',
			errorPropKey = 'error',
			errorFields = [key],
			error: outerError = false,
			errorOnlyIfTouched = false,
			helperText,
			fallbackValue = type === 'text' || type === 'number' ? '' : type === 'checkbox' ? false : undefined,
			transformValue = type === 'number'
				? (v: any) => (v ? Number(digits ? Number(v).toFixed(digits) : v) : v)
				: type === 'checkbox'
				? (v: any) => (typeof v === 'string' ? v === 'true' : Boolean(v))
				: type === 'datetime-local'
				? (v: any) => (v && isValid(v) ? new Date(v) : v)
				: (v: any) => v,
			transformInputValue = transformValue,
			digits,
			passType = true,
			ignoreOriginalOnChange = false,
			ignoreOriginalOnBlur = false,
			// these defaults are based on bug researches
			// if original value is needed set config value explicitly
			// prevents strings (unparsed values) to be passed to state
			passRef = type !== 'checkbox', // for switch/checkbox input
			passOnBlur = !['number', 'checkbox', 'text'].includes(type), // for number input
			errorHintPropKey = 'helperText',
			onChangeAfterTransform,
			getEventValue = e => (e.target as any)[type === 'checkbox' ? 'checked' : 'value'],
		} = options;

		const onBlur =
			options.onBlur ?? form.control._options.mode === 'onBlur'
				? () => form.trigger(key)
				: undefined;

		const regResult = form.register(key, {
			...options,
			onBlur,
			onChange:
				options.onChange ??
				function (e: SyntheticEvent) {
					const rawValue = getEventValue?.(e);

					let value = (transformInputValue ? transformInputValue(rawValue) : rawValue) ?? fallbackValue;

					if (
						type === 'number' &&
						typeof value === 'number' &&
						[typeof options.max, typeof options.min, typeof options.step].includes('number')
					) {
						if (typeof options.step === 'number') {
							value = parseFloat((Math.round(value / options.step) * options.step).toFixed(digits ?? 2));
						}

						if (typeof options.max === 'number' && value > options.max) {
							value = options.max;
						} else if (typeof options.min === 'number' && value < options.min) {
							value = options.min;
						}
					}

					if (
						type === 'text' &&
						typeof value === 'string' &&
						[typeof options.maxLength, typeof options.minLength].includes('number')
					) {
						if (typeof options.maxLength === 'number' && value.length > options.maxLength) {
							value = value.slice(0, options.maxLength);
						} else if (typeof options.minLength === 'number' && value.length < options.minLength) {
							value = value.padEnd(options.minLength, ' ');
						}
					}

					if (
						type === 'datetime-local' &&
						value instanceof Date &&
						[options.maxDate, options.minDate].some(d => d instanceof Date)
					) {
						if (options.maxDate instanceof Date && value > options.maxDate) {
							value = options.maxDate;
						} else if (options.minDate instanceof Date && value < options.minDate) {
							value = options.minDate;
						}
					}

					if (onChangeAfterTransform) {
						onChangeAfterTransform(value);
					} else {
						form.setValue(key, value);
					}
				},
		});

		const isTouched = get(form.formState.touchedFields, key);

		const error = errorFields?.map(field => get(form.formState.errors, field)).find(Boolean);

		const hasError: boolean = errorOnlyIfTouched && !isTouched ? false : !!error || !!outerError;

		const onChangeProp = ignoreOriginalOnChange ? options.onChange : regResult.onChange;

		const onBlurProp = passOnBlur ? regResult.onBlur : onBlur;

		const originalValue = form.watch(key);

		const transformedValue = (transformValue ? transformValue(originalValue) : originalValue) ?? fallbackValue;

		return {
			...regResult,
			ref: passRef ? regResult.ref : undefined,
			type: passType ? type : undefined,
			name: key,
			onBlur: onBlurProp,
			onChange: onChangeProp,
			value: transformedValue,
			[changePropKey]: onChangeProp,
			[valuePropKey]: transformedValue,
			[errorPropKey]: hasError,
			...(errorHintPropKey && hasError && (error || helperText)
				? { [errorHintPropKey]: error?.message || helperText }
				: {}),
		};
	};

	return { register };
};
